/**
 * Settings Hook - Custom hook for settings operations
 * @file src/web/features/settings/hooks/useSettings.ts
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../../auth';
import { config } from '../../auth/config';
import type { Settings, UpdateSettingsDto } from '../types/settings.types';

export function useSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const makeAuthenticatedRequest = useCallback(async (
    endpoint: string,
    options: RequestInit = {}
  ) => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${config.api.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
        [config.auth.headers.auth]: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        // If we can't parse the error response, use the generic message
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }, [token]);

  const getSettings = async (): Promise<Settings | null> => {
    setLoading(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('Not authenticated');
      }
      const data = await makeAuthenticatedRequest('/api/settings');
      return data.settings;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (settingsData: UpdateSettingsDto): Promise<Settings | null> => {
    setLoading(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('Not authenticated');
      }
      const data = await makeAuthenticatedRequest('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(settingsData),
      });
      return data.settings;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    getSettings,
    updateSettings,
  };
}
